import { QAlert, deprecated, QSwitch, QText, QFlex } from '@qualio/ui-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ConfirmationModal } from '../../containers/confirmation-modal';
import '../../styles/CompanyFeature.css';
import { FeatureStatus, UserGroupPermissions } from '../../types';
import { getFeatureAccessName, isFeature } from '../../utils/translator';
import { FeatureToggleLabels, UsergroupsList } from '../../utils/umfe.enums';
import { AccessTable } from './AccessTable';

const AlertContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0px;
`;

type FeatureProps = {
  id: string;
  featureId: string;
  name: string;
  info: string;
  setFeatureStatus: (active: FeatureStatus) => void;
  isActive: boolean;
  userGroupPermissions: UserGroupPermissions[];
  isStarterCompany: boolean;
};

export const FeatureComponent: React.FC<FeatureProps> = ({
  id,
  featureId,
  name,
  info,
  setFeatureStatus,
  isActive,
  userGroupPermissions,
  isStarterCompany,
}) => {
  const { t } = useTranslation();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const tableColumns: deprecated.QDataColumn[] = [
    {
      Header: t('features.role'),
      accessor: 'name',
      width: '20%',
    },
    {
      Header: t('features.access'),
      accessor: 'access',
      width: '80%',
    },
  ];
  const tableData = userGroupPermissions
    .map((userGroup) => {
      const { userGroup: role } = userGroup;
      const accessString =
        userGroup.userGroupPermissions.length === 0
          ? t('features.none')
          : userGroup.userGroupPermissions
              .filter((usergroupPermission) => isFeature(usergroupPermission.permission.permission))
              .map((usergroupPermission) => {
                return getFeatureAccessName(usergroupPermission.permission.permission);
              })
              .join(', ');
      const titlecasedRole = role.charAt(0).toUpperCase() + role.substring(1);
      const { sort, labelKey } = Object.values(UsergroupsList).find((usergroup) => usergroup.value === role) || {
        labelKey: titlecasedRole,
        sort: 0,
      };
      return {
        name: labelKey ? t(labelKey) : titlecasedRole,
        access: accessString,
        sort,
      };
    })
    .sort((a, b) => a.sort - b.sort);
  const handleConfirmationModalChange = () => {
    setConfirmationModalOpen(true);
  };
  const handleChange = () => {
    setFeatureStatus({
      id,
      featureId,
      buttonStatus: !isActive,
    });
    setConfirmationModalOpen(false);
  };

  return (
    <QFlex direction="column">
      <div className="feature-title">
        <QText fontSize="md" as="h6">
          {name}
        </QText>
      </div>
      <div className="feature-info">
        <QText fontSize="sm" fontWeight="normal">
          {info}
        </QText>
        <QSwitch
          isChecked={isActive}
          onChange={!isActive ? handleChange : handleConfirmationModalChange}
          isDisabled={isStarterCompany}
          rightLabel={FeatureToggleLabels[featureId] || t('features.enable')}
        />
        {isStarterCompany ? (
          <AlertContainer>
            <QAlert description={t('features.upgradeMessage')} status="info" />
          </AlertContainer>
        ) : (
          <AccessTable columns={tableColumns} rows={tableData} />
        )}
        {confirmationModalOpen && (
          <ConfirmationModal
            closeModal={() => setConfirmationModalOpen(false)}
            title={t('features.disable', { name })}
            buttonProps={{
              type: 'button',
              label: t('features.yesDisable', { name }),
              isDestructive: true,
              onClick: handleChange,
            }}
          >
            {t('features.disableConfirmation')}
          </ConfirmationModal>
        )}
      </div>
    </QFlex>
  );
};
